import { Component, effect, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { SelectWorkplaceDialogStore } from './select-workplace-dialog.store';
import { Validators } from '@angular/forms';
import { isInvalid } from '../../../../../shared/helpers/validate';
import { currentFolderWorkplace } from '../../../../../../utils/folder.utils';
import { dateifyOrNow } from 'packages/client/src/app/modules/shared/helpers/date.helper';
import { DateInputComponent } from '../../../../../../components/kit/input/date-input/date-input.component';
import { ActionButtonsComponent } from '../../../../../../components/kit/action-buttons/action-buttons.component';
import { WorkplaceSelectComponent } from '../../../services/workplace/workplace-select/workplace-select.component';
import { BaseDialogComponent } from '../../../../../../components/kit/base-dialog/base-dialog.component';
import { AppFormGroup, changeSignal } from '../../../../../../utils/form.utils';
import { fb } from '../../../../../shared/shared.global-service';
import { contentGlobalSuccess } from '../../../../content.error';
import { MapService } from '../../../../../shared/components/map/map.service';
import { WarnFloatMessageComponent } from '../../../../../../components/kit/float-message/warn-float-message.component';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';

@Component({
    selector: 'app-select-workplace-dialog',
    templateUrl: 'select-workplace-dialog.component.html',
    styleUrl: 'select-workplace-dialog.component.scss',
    providers: [SelectWorkplaceDialogStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DateInputComponent,
        ActionButtonsComponent,
        WorkplaceSelectComponent,
        WarnFloatMessageComponent,
    ],
})
export class SelectWorkplaceDialogComponent extends BaseDialogComponent {
    public readonly store = inject(SelectWorkplaceDialogStore);
    private readonly mapService = inject(MapService);

    protected prefix = 'folder.workplace.';

    protected form = new AppFormGroup({
        validFrom: fb.control<Date | null>(null),
        validTo: fb.control<Date | null>(null),
        workplaceId: fb.control<number | null>(null, Validators.required),
    });

    protected readonly workplaceIdChange = changeSignal(this.form.controls.workplaceId);
    protected isNotClosestWorkplace = signal<boolean | null>(null);

    constructor() {
        super();

        effect(() => {
            const folder = this.store.folder();

            this.form.patchValue({
                validFrom: dateifyOrNow(currentFolderWorkplace(folder)?.dateFrom),
                workplaceId: currentFolderWorkplace(folder)?.workplaceId || null,
            });
        });

        effect(async () => {
            const workplaceId = this.workplaceIdChange();
            const folder = this.store.folder();
            const workplaces = this.store.service()?.workplaces || [];

            const personAddress = folder?.person?.currentAddress?.address;

            if (!workplaceId || !personAddress || workplaces.length <= 1) {
                this.isNotClosestWorkplace.set(null);
                return;
            }

            let closestId: number | null = null;
            let closestDist = 999_999;
            for (const wp of workplaces) {
                if (!wp.address) continue;
                const dis = await this.mapService.getPath(wp.address, personAddress);
                if (dis?.length != null && dis.length < closestDist) {
                    closestDist = dis.length;
                    closestId = wp.id;
                }
            }

            this.isNotClosestWorkplace.set(closestId !== null && closestId !== workplaceId);
        });
    }

    public override async save(): Promise<void> {
        if (isInvalid(this.form)) return;

        this.store.api.assignWorkplaceToFolder({
            workplaceId: this.form.value.workplaceId || 0,
            folderId: this.store.folder()?.id || 0,
            dateFrom: this.form.value.validFrom?.toISOString() ?? (new Date()).toISOString(),
            dateTo: this.form.value.validTo?.toISOString() ?? null,
        }).then(() => {
            contentGlobalSuccess.next('common.assigned');
            this.dialogRef.close(true);
        });
    }

}
